import { useCallback } from "react";

import { useWebSocketContext } from "../providers/WebSocketProvider";

import type { BaseSocketMessage, WsEvent } from "@repo/shared";

export const useWebSocket = () => {
  const { socket } = useWebSocketContext();

  const subscribe = useCallback(
    <T extends BaseSocketMessage>(event: WsEvent, callback: (data: T) => void) => {
      if (!socket) {
        console.warn(`[WebSocket] Unable to subscribe to '${event}' - socket is not connected`);
        return undefined;
      }

      const wrappedCallback = (data: unknown) => {
        console.debug(`[WebSocket] Received '${event}':`, data);
        callback(data as T);
      };

      socket.on(event, wrappedCallback);

      return () => {
        socket.off(event, wrappedCallback);
      };
    },
    [socket],
  );

  const emit = useCallback(
    (event: WsEvent, data?: unknown) => {
      if (!socket) {
        console.warn(`[WebSocket] Unable to emit '${event}' - socket is not available`);
        return false;
      }

      if (!socket.connected) {
        console.warn(`[WebSocket] Unable to emit '${event}' - socket is not connected`);
        return false;
      }

      socket.emit(event, data);
      return true;
    },
    [socket],
  );

  return {
    socket,
    subscribe,
    emit,
  };
};
